import { MutationTree } from "vuex";
import {
  IApplicationState,
  IApplicationsDto,
  IApplicationDto,
  IApplicationStatuses,
  IDraftsDto,
  IIfcsDetails
} from "./types";
import Vue from "vue";
import { cloneDeep } from "@/helpers/object";
import { ITimelineItem } from "../task/types";
import { isNull, isNullOrUndefined } from "util";

export namespace APPLICATION_MUTATIONS {
  export const TOGGLE_IS_LOADING = "toggleIsLoading";
  export const SET_CREDIT_OUTCOME = "setCreditOutcome";
  export const SET_CREDIT_DECISION_CHANGE_REASON = "setcreditDecisionChangeReason";
  export const SET_CREDIT_LIMIT = "setCreditLimit";
  export const RESET_APPLICATION_DATA_STATE = "resetApplicationDataState";
  export const SET_APPLICATION_LIST = "setApplicationList";
  export const SET_APPLICATION_LIST_TOTAL = "setApplicationListTotal";
  export const SET_COMPLETED_APPLICATION_LIST = "setCompletedApplicationList";
  export const SET_COMPLETED_APPLICATION_LIST_TOTAL = "setCompletedApplicationListTotal";
  export const SET_MY_APPLICATION_LIST = "setMyApplicationList";
  export const SET_MY_APPLICATION_LIST_TOTAL = "setMyApplicationListTotal";
  export const SET_DRAFTS_LIST = "setDraftsList";
  export const SET_DRAFTS_LIST_TOTAL = "setDraftsListTotal";
  export const RESET_STATE = "resetState";
  export const SET_APPLICATION_DATA = "setApplicationData";
  export const SET_IFCS_DATA = "setIfcsData";
  export const SET_INVOICE_EMAIL = "setApplicationInvoiceEmail";
  export const SET_INVOICE_DATE = "setApplicationInvoiceDate";
  export const SET_PAYMENT_PLAN = "setApplicationPaymentPlan";
  export const SET_APPLICANT_EMAIL_ADDRESS = "setApplicationApplicantEmailAddress";
  export const SET_LOYALTY_TYPE = "setApplicationLoyaltyType";
  export const SET_FLY_BUYS_NUMBER = "setFlyBuysNumber";
  export const SET_AIRPOINTS_NUMBER = "setAirPointsNumber";
  export const SET_AIRPOINTS_FIRST_NAME = "setAirPointsFirstName";
  export const SET_AIRPOINTS_LAST_NAME = "setAirPointsLastName";
  export const SET_DELIVERY_ADDRESS = "setDeliveryAddress";
  export const SET_DELIVERY_ADDRESS_TEXT = "setDeliveryAddressText";
  export const SET_HAS_MANUAL_DELIVERY_ADDRESS = "setHasManualDeliveryAddress";
  export const SET_PHYSICAL_ADDRESS = "setPhysicalAddress";
  export const SET_PHYSICAL_ADDRESS_TEXT = "setPhysicalAddressText";
  export const SET_HAS_MANUAL_PHYSICAL_ADDRESS = "setHasManualPhysicalAddress";
  export const SET_RESIDENTIAL_ADDRESS = "setResidentialAddress";
  export const SET_RESIDENTIAL_ADDRESS_TEXT = "setResidentialAddressText";
  export const SET_HAS_MANUAL_RESIDENTIAL_ADDRESS = "setHasManualResidentialAddress";
  export const SET_JDE_ACCOUNT_NUMBER = "setJdeAccountNumber";
  export const SET_IFCS_ACCOUNT_NUMBER = "setIfcsAccountNumber";
  export const SET_STATUS = "setStatus";
  export const SET_CANCELLATION_REASON = "setCancellationReason";
  export const SET_REQUEST_FOR_CALLBACK_TASK = "setRequestForCallbackTask";
  export const SET_PHYSICAL_ADDRESS_LOOKUP = "setPhysicalAddressLookup";
  export const SET_PHYSICAL_ADDRESS_LOOKUP_IS_LOADING = "setPhysicalAddressLookupIsLoading";
  export const SET_DELIVERY_ADDRESS_LOOKUP = "setDeliveryAddressLookup";
  export const SET_DELIVERY_ADDRESS_LOOKUP_IS_LOADING = "setDeliveryAddressLookupIsLoading";
  export const SET_CREDIT_OUTCOME_TASK = "setCreditOutcomeTask";
  export const SET_FOLLOW_UP_PERSONAL_GUARANTEE_TASK = "setFollowUpPersonalGuaranteeTask";
  export const SET_CARD_ACCOUNT_SETUP_TASK = "setCardAccountSetupTask";
  export const SET_MODIFY_ACCOUNT_NUMBER_TASK = "setModifyAccountNumberTask";
  export const SET_JDE_ACCOUNT_SETUP_TASK = "setJdeAccountSetupTask";
  export const TOGGLE_ASSIGN_TO_ME_CONFIRMATION = "toggleAssignToMeConfirmation";
  export const SET_MY_APPLICATIONS_DATA_TABLE_STATUS_FILTERS = "setMyApplicationsDataTableStatusFilters";
  export const SET_ALL_APPLICATIONS_DATA_TABLE_STATUS_FILTERS = "setAllApplicationsDataTableStatusFilters";
  export const SET_COMPLETED_DATA_TABLE_STATUS_FILTERS = "setCompletedDataTableStatusFilters";

  export const SET_MY_APPLICATIONS_PAGINATION = "setMyApplicationsPagination";
  export const SET_ALL_APPLICATIONS_PAGINATION = "setAllApplicationsPagination";
  export const SET_DRAFT_APPLICATIONS_PAGINATION = "setDraftApplicationsPagination";
  export const SET_COMPLETED_APPLICATIONS_PAGINATION = "setCompletedApplicationsPagination";
  export const SET_ACTIVE_APPLICATIONS_TAB = "setActiveApplicationsTab";

  export const SET_IFCS_PHYSICAL_STREET = "setIfcsPhysicalStreet";
  export const SET_IFCS_PHYSICAL_TOWN = "setIfcsPhysicalTown";
  export const SET_IFCS_PHYSICAL_POSTCODE = "setIfcsPhysicalPostCode";
  export const SET_IFCS_PHYSICAL_COUNTRY = "setIfcsPhysicalCountry";
  export const SET_IFCS_POSTAL_STREET = "setIfcsPostalStreet";
  export const SET_IFCS_POSTAL_TOWN = "setIfcsPostalTown";
  export const SET_IFCS_POSTAL_POSTCODE = "setIfcsPostalPostCode";
  export const SET_IFCS_POSTAL_COUNTRY = "setIfcsPostalCountry";
}

// Allows for setting nested state on an object that might not exist
function setNestedState(state: any, paths: string[], value: any) {
  paths.reduce((stateToReduce, path, index) => {
    if (!stateToReduce) {
      return;
    }
    // if it's the last value in the paths array don't need to create a new object
    if (index === paths.length - 1) {
      Vue.set(stateToReduce, path, value);
    } else if (!stateToReduce.hasOwnProperty(path) || stateToReduce[path] == null) {
      Vue.set(stateToReduce, path, {});
    }

    return stateToReduce[path];
  }, state);
}

export const mutations: MutationTree<IApplicationState> = {
  [APPLICATION_MUTATIONS.TOGGLE_ASSIGN_TO_ME_CONFIRMATION](state, show) {
    state.showAssignToMeConfirmation = show;
  },
  [APPLICATION_MUTATIONS.TOGGLE_IS_LOADING](
    state: IApplicationState,
    isLoading: boolean
  ): void {
    state.isLoading = isLoading;
  },
  [APPLICATION_MUTATIONS.SET_CREDIT_DECISION_CHANGE_REASON](
    state: IApplicationState,
    creditDecisionChangeReason
  ): void {
    if (state.applicationData) {
      state.applicationData.creditDecisionChangeReason = creditDecisionChangeReason;
    }
  },
  [APPLICATION_MUTATIONS.SET_CREDIT_LIMIT](
    state: IApplicationState,
    creditLimit
  ): void {
    if (state.applicationData) {
      state.applicationData.creditLimit = creditLimit;
    }
  },
  [APPLICATION_MUTATIONS.RESET_APPLICATION_DATA_STATE](
    state: IApplicationState
  ): void {
    Vue.set(state, "applicationData", undefined);
    Vue.set(state, "applicationDataOriginal", undefined);
    Vue.set(state, "deliveryAddressLookup", null);
    Vue.set(state, "physicalAddressLookup", null);
  },
  [APPLICATION_MUTATIONS.SET_APPLICATION_LIST](
    state: IApplicationState,
    applicationsList: IApplicationsDto[]
  ): void {
    state.applicationsList = applicationsList;
  },
  [APPLICATION_MUTATIONS.SET_APPLICATION_LIST_TOTAL](
    state: IApplicationState,
    total: number
  ): void {
    state.applicationsListTotal = total;
  },
  [APPLICATION_MUTATIONS.SET_COMPLETED_APPLICATION_LIST](
    state: IApplicationState,
    completedApplicationsList: IApplicationsDto[]
  ): void {
    state.completedApplicationsList = completedApplicationsList;
  },
  [APPLICATION_MUTATIONS.SET_COMPLETED_APPLICATION_LIST_TOTAL](
    state: IApplicationState,
    total: number
  ): void {
    state.completedApplicationsListTotal = total;
  },
  [APPLICATION_MUTATIONS.SET_MY_APPLICATION_LIST](
    state: IApplicationState,
    applicationsList: IApplicationsDto[]
  ): void {
    state.myApplicationsList = applicationsList;
  },
  [APPLICATION_MUTATIONS.SET_MY_APPLICATION_LIST_TOTAL](
    state: IApplicationState,
    total: number
  ): void {
    state.myApplicationsListTotal = total;
  },
  [APPLICATION_MUTATIONS.SET_DRAFTS_LIST](
    state: IApplicationState,
    draftsList: IDraftsDto[]
  ): void {
    state.draftsList = draftsList;
  },
  [APPLICATION_MUTATIONS.SET_DRAFTS_LIST_TOTAL](
    state: IApplicationState,
    total: number
  ): void {
    state.draftsListTotal = total;
  },
  [APPLICATION_MUTATIONS.SET_APPLICATION_DATA](
    state: IApplicationState,
    applicationData: IApplicationDto
  ): void {
    Vue.set(state, "applicationData", applicationData);
    Vue.set(state, "applicationDataOriginal", cloneDeep(applicationData));
  },
  [APPLICATION_MUTATIONS.SET_IFCS_DATA](
    state: IApplicationState,
    ifcsData: IIfcsDetails
  ): void {
    Vue.set(state, "ifcsOverrideData", ifcsData);
    if (isNullOrUndefined(ifcsData)) Vue.set(state, "ifcsOverrideDataOriginal", ifcsData);
    else Vue.set(state, "ifcsOverrideDataOriginal", cloneDeep(ifcsData));
  },
  [APPLICATION_MUTATIONS.SET_INVOICE_EMAIL](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "invoiceEmail"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_INVOICE_DATE](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "invoiceDate"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_PAYMENT_PLAN](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "paymentPlan"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_APPLICANT_EMAIL_ADDRESS](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["applicationData", "email"], value);
  },
  [APPLICATION_MUTATIONS.SET_LOYALTY_TYPE](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "loyaltyDetails", "pointType"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_FLY_BUYS_NUMBER](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "loyaltyDetails", "flyBuysNumber"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_AIRPOINTS_NUMBER](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "loyaltyDetails", "airPointsNumber"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_AIRPOINTS_FIRST_NAME](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "loyaltyDetails", "airPointsFirstName"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_AIRPOINTS_LAST_NAME](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "loyaltyDetails", "airPointsLastName"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "deliveryAddress"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_TEXT](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "deliveryAddressText"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_HAS_MANUAL_DELIVERY_ADDRESS](
    state: IApplicationState,
    value: boolean
  ): void {
    setNestedState(
      state,
      ["applicationData", "finalDetails", "hasManualDeliveryAddress"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "businessDetails", "physicalAddress"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_TEXT](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "businessDetails", "physicalAddressText"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_HAS_MANUAL_PHYSICAL_ADDRESS](
    state: IApplicationState,
    value: boolean
  ): void {
    setNestedState(
      state,
      ["applicationData", "businessDetails", "hasManualPhysicalAddress"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_RESIDENTIAL_ADDRESS](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "creditDetails", "residentialAddress"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_RESIDENTIAL_ADDRESS_TEXT](
    state: IApplicationState,
    value
  ): void {
    setNestedState(
      state,
      ["applicationData", "creditDetails", "residentialAddressText"],
      value
    );
  },
  [APPLICATION_MUTATIONS.SET_HAS_MANUAL_RESIDENTIAL_ADDRESS](
    state: IApplicationState,
    value: boolean
  ): void {
    setNestedState(
      state,
      ["applicationData", "creditDetails", "hasManualResidentialAddress"],
      value
    );
  },

  [APPLICATION_MUTATIONS.SET_IFCS_PHYSICAL_STREET](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "physicalAddress", "line1"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_PHYSICAL_TOWN](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "physicalAddress", "line2"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_PHYSICAL_POSTCODE](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "physicalAddress", "postCode"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_PHYSICAL_COUNTRY](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "physicalAddress", "country"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_POSTAL_STREET](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "postalAddress", "line1"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_POSTAL_TOWN](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "postalAddress", "line2"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_POSTAL_POSTCODE](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "postalAddress", "postCode"], value);
  },
  [APPLICATION_MUTATIONS.SET_IFCS_POSTAL_COUNTRY](
    state: IApplicationState,
    value
  ): void {
    setNestedState(state, ["ifcsOverrideData", "postalAddress", "country"], value);
  },

  [APPLICATION_MUTATIONS.SET_STATUS](
    state: IApplicationState,
    value: IApplicationStatuses
  ): void {
    if (state.applicationData) {
      state.applicationData.status = value;
    }
  },
  [APPLICATION_MUTATIONS.SET_CANCELLATION_REASON](
    state: IApplicationState,
    reason: IApplicationStatuses
  ): void {
    if (state.applicationData) {
      state.applicationData.cancellationReason = reason;
    }
  },
  [APPLICATION_MUTATIONS.SET_JDE_ACCOUNT_NUMBER](
    state: IApplicationState,
    accountNumber
  ): void {
    if (state.applicationData) {
      state.applicationData.jdeAccountNumber = accountNumber;
    }
  },
  [APPLICATION_MUTATIONS.SET_IFCS_ACCOUNT_NUMBER](
    state: IApplicationState,
    accountNumber
  ): void {
    if (state.applicationData) {
      state.applicationData.ifcsAccountNumber = accountNumber;
    }
  },
  [APPLICATION_MUTATIONS.SET_REQUEST_FOR_CALLBACK_TASK](
    state: IApplicationState,
    task
  ): void {
    state.requestForCallbackTask = task;
  },
  [APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_LOOKUP](
    state: IApplicationState,
    physicalAddressLookup
  ): void {
    Vue.set(state, "physicalAddressLookup", physicalAddressLookup);
  },
  [APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_LOOKUP_IS_LOADING](
    state: IApplicationState,
    physicalAddressLookupIsLoading
  ): void {
    state.physicalAddressLookupIsLoading = physicalAddressLookupIsLoading;
  },
  [APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_LOOKUP](
    state: IApplicationState,
    deliveryAddressLookup
  ): void {
    Vue.set(state, "deliveryAddressLookup", deliveryAddressLookup);
  },
  [APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_LOOKUP_IS_LOADING](
    state: IApplicationState,
    deliveryAddressLookupIsLoading
  ): void {
    state.deliveryAddressLookupIsLoading = deliveryAddressLookupIsLoading;
  },
  [APPLICATION_MUTATIONS.SET_CREDIT_OUTCOME_TASK](
    state: IApplicationState,
    task
  ): void {
    state.creditOutcomeTask = task;
  },
  [APPLICATION_MUTATIONS.SET_FOLLOW_UP_PERSONAL_GUARANTEE_TASK](
    state: IApplicationState,
    task: ITimelineItem
  ): void {
    state.followUpPersonalGuaranteeTask = task;
  },
  [APPLICATION_MUTATIONS.SET_CARD_ACCOUNT_SETUP_TASK](
    state: IApplicationState,
    task: ITimelineItem
  ): void {
    state.cardAccountSetupTask = task;
  },
  [APPLICATION_MUTATIONS.SET_MODIFY_ACCOUNT_NUMBER_TASK](
    state: IApplicationState,
    task: ITimelineItem
  ): void {
    state.modifyAccountNumberTask = task;
  },
  [APPLICATION_MUTATIONS.SET_JDE_ACCOUNT_SETUP_TASK](
    state: IApplicationState,
    task: ITimelineItem
  ): void {
    state.jdeAccountSetupTask = task;
  },
  [APPLICATION_MUTATIONS.SET_MY_APPLICATIONS_DATA_TABLE_STATUS_FILTERS](
    state,
    value
  ) {
    state.dataTables.myApplications.statusFilters = [...value];
  },
  [APPLICATION_MUTATIONS.SET_ALL_APPLICATIONS_DATA_TABLE_STATUS_FILTERS](
    state,
    value
  ) {
    state.dataTables.allApplications.statusFilters = [...value];
  },
  [APPLICATION_MUTATIONS.SET_COMPLETED_DATA_TABLE_STATUS_FILTERS](
    state,
    value
  ) {
    state.dataTables.completed.statusFilters = [...value];
  },
  [APPLICATION_MUTATIONS.SET_MY_APPLICATIONS_PAGINATION](state, value) {
    state.dataTables.myApplications.pagination = value;
  },
  [APPLICATION_MUTATIONS.SET_ALL_APPLICATIONS_PAGINATION](state, value) {
    state.dataTables.allApplications.pagination = value;
  },
  [APPLICATION_MUTATIONS.SET_DRAFT_APPLICATIONS_PAGINATION](state, value) {
    state.dataTables.drafts.pagination = value;
  },
  [APPLICATION_MUTATIONS.SET_COMPLETED_APPLICATIONS_PAGINATION](state, value) {
    state.dataTables.completed.pagination = value;
  },
  [APPLICATION_MUTATIONS.SET_ACTIVE_APPLICATIONS_TAB](state, value) {
    state.activeApplicationTab = value;
  },
};
